import React, { useEffect, useRef, useState } from "react";
import { createGame, setAim, setPower, shoot, tick } from "./engine.js";
import type { CarromGame } from "./types.js";
import { BOARD_W, BOARD_H, POCKETS, POCKET_R, DISC_R, STRIKER_R } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

export default function CarromPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [game, setGame] = useState<CarromGame>(() => createGame());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const dragging = useRef(false);

  gameRef.current = game;

  function resetGame() {
    setGame(createGame());
  }

  function render(ctx: CanvasRenderingContext2D, g: CarromGame) {
    ctx.clearRect(0, 0, BOARD_W, BOARD_H);

    ctx.fillStyle = "#5a3a1a";
    ctx.fillRect(0, 0, BOARD_W, BOARD_H);

    ctx.fillStyle = "#c8a050";
    ctx.fillRect(6, 6, BOARD_W - 12, BOARD_H - 12);

    for (const p of POCKETS) {
      ctx.fillStyle = "#3a2a0a";
      ctx.beginPath();
      ctx.arc(p.x, p.y, POCKET_R, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.strokeStyle = "#8a6030";
    ctx.lineWidth = 2;
    ctx.strokeRect(12, 12, BOARD_W - 24, BOARD_H - 24);

    for (const d of g.discs) {
      if (d.pocketed) continue;
      ctx.fillStyle = d.color === "white" ? "#f0e8d0" : d.color === "black" ? "#222" : "#e74c3c";
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = d.color === "white" ? "#999" : "#000";
      ctx.lineWidth = 1;
      ctx.stroke();
      if (d.color === "red") {
        ctx.fillStyle = "#fff";
        ctx.font = "8px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Q", d.x, d.y + 3);
      }
    }

    if (!g.striker.pocketed) {
      ctx.fillStyle = "#4466aa";
      ctx.beginPath();
      ctx.arc(g.striker.x, g.striker.y, g.striker.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#224488";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    if (!g.inMotion && g.winner === null) {
      const angleRad = (g.aimAngle * Math.PI) / 180;
      const lineLen = 60;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "rgba(255,255,255,0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(g.striker.x, g.striker.y);
      ctx.lineTo(g.striker.x + Math.cos(angleRad) * lineLen, g.striker.y + Math.sin(angleRad) * lineLen);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.fillStyle = "#fff";
    ctx.font = "14px monospace";
    ctx.fillText(`P1: ${g.scores[0]}  P2: ${g.scores[1]}`, 10, 20);
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    render(ctx, game);
  }, [game]);

  useEffect(() => {
    if (!game.inMotion) return;
    lastTimeRef.current = performance.now();
    function loop(time: number) {
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;
      setGame((prev) => tick(prev, dt));
      if (gameRef.current.inMotion) {
        rafRef.current = requestAnimationFrame(loop);
      }
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [game.inMotion]);

  function handleCanvasDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (game.inMotion || game.winner !== null) return;
    dragging.current = true;
    updateAim(e);
  }

  function handleCanvasMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!dragging.current) return;
    updateAim(e);
  }

  function updateAim(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = (e.clientX - rect.left) * (BOARD_W / rect.width);
    const my = (e.clientY - rect.top) * (BOARD_H / rect.height);
    const dx = mx - game.striker.x;
    const dy = my - game.striker.y;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const power = Math.min(100, dist * 0.5);
    setGame((prev) => setPower(setAim(prev, angle), power));
  }

  function handleCanvasUp() {
    if (!dragging.current) return;
    dragging.current = false;
    setGame((prev) => shoot(prev));
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== null} winnerName={game.winner ? `Player ${game.winner}` : undefined} />
      {!hideTitle && <h1 className="title">Carrom</h1>}

      <section className="control-panel">
        <p className="status">{game.message}</p>
        <div className="actions">
          <button onClick={resetGame}>New Game</button>
        </div>
      </section>

      <section className="board-panel">
        <canvas
          ref={canvasRef}
          width={BOARD_W}
          height={BOARD_H}
          className="carrom-canvas"
          onMouseDown={handleCanvasDown}
          onMouseMove={handleCanvasMove}
          onMouseUp={handleCanvasUp}
          onMouseLeave={handleCanvasUp}
        />
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Click and drag from the striker to aim and set power.</p>
          <p>2. Release to shoot. Pocket your color discs.</p>
          <p>3. Player 1 pockets white, Player 2 pockets black. Red (Queen) is +3!</p>
        </div>
      </section>
    </div>
  );
}
